import { List } from 'immutable';
import parseLegTypeFromTradeRef from '../strategy/parseLegTypeFromTradeRef';
import {
  LegType,
  Strategy,
  TradeReference,
} from '@oai-frontend-monorepo/types';
import parseStrikeFromSymbol from '../symbol/parseStrikeFromSymbol';

export type SpreadWidth = number | 'Unbound';

const getSpreadWidth = (
  tradeRef: TradeReference,
  returnMinSpread = false
): SpreadWidth | undefined => {
  const strategy = tradeRef.strategy;

  switch (strategy) {
    case Strategy.SHORT_PUT: {
      if (!tradeRef.symbols?.length) {
        return;
      }

      const strike = parseStrikeFromSymbol(tradeRef.symbols?.[0]);

      return strike;
    }

    case Strategy.LONG_CALL:
    case Strategy.LONG_PUT: {
      return 'Unbound';
    }

    case Strategy.IRON_CONDOR:
    case Strategy.IRON_BUTTERFLY: {
      const legTypes = List(tradeRef.symbols)?.map((s) => ({
        type: parseLegTypeFromTradeRef({ symbol: s, tradeRef }),
        symbol: s,
      }));

      const calls = legTypes
        ?.filter(
          (l) => l.type === LegType.SHORT_CALL || l.type === LegType.LONG_CALL
        )
        .map((l) => l.symbol);

      const puts = legTypes
        ?.filter(
          (l) => l.type === LegType.SHORT_PUT || l.type === LegType.LONG_PUT
        )
        .map((l) => l.symbol);

      const width1 = getSpreadWidth({
        strategy: Strategy.SHORT_CALL_SPREAD,
        symbols: calls.toArray(),
        underlyingSymbol: '',
      }) as number;

      const width2 = getSpreadWidth({
        strategy: Strategy.SHORT_PUT_SPREAD,
        symbols: puts.toArray(),
        underlyingSymbol: '',
      }) as number;

      if (!width1 || !width2) {
        return;
      }

      return returnMinSpread
        ? Math.min(width1, width2)
        : Math.max(width1, width2);
    }

    default: {
      if (!tradeRef.symbols) {
        return;
      }

      const strikes = tradeRef.symbols?.map(parseStrikeFromSymbol);

      const [strike1, strike2] = strikes;

      if (!strike1 || !strike2) {
        return undefined;
      }

      return Math.abs(strike1 - strike2);
    }
  }
};

export default getSpreadWidth;
